// Desktop "Aura Analysis" dashboard — Stitch reference layout.
// Pure presentation: receives all state + handlers from AuraHome.tsx.
// Logic, providers, credits, scoring all stay in the parent.
import { ReactNode, useEffect, useState } from "react";
import {
  Bell, Camera, Upload, X, Loader2, Wand2, Sparkles,
  BrainCircuit, Palette, Zap, ArrowRight, Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useSignedImage } from "@/hooks/useSignedImage";
import { CreditsChip } from "@/components/CreditsChip";
import { StitchDesktopHeader } from "@/components/stitch/StitchDesktopHeader";

interface AuraResult {
  tryonId: string;
  aura_score: number;
  aura_silhouette: number;
  aura_proportions: number;
  aura_color_season: number;
  aura_statement: number;
  aura_photo_readiness: number;
  aura_verdict: string;
  aura_tips: string[];
}

interface Props {
  // Photo state
  photoPreview: string | null;
  scanning: boolean;
  scanLabel: string;          // current SCAN_STAGES[stage]
  result: AuraResult | null;

  // Handlers (defined in parent)
  onPickCamera: () => void;
  onPickUpload: () => void;
  onClearPhoto: () => void;
  onRunAura: () => void;
  onNavHistory: () => void;

  // Header
  avatarUrl: string;
  initials: string;
  onBuyCredits: () => void;
}

/** Map our 5 editorial sub-scores to the reference's coloured metric bars. */
const METRICS: Array<{ key: keyof AuraResult; label: string; bar: string; text: string }> = [
  { key: "aura_silhouette",       label: "Silhouette",      bar: "from-fuchsia-500 to-pink-400", text: "text-fuchsia-300" },
  { key: "aura_proportions",      label: "Proportions",     bar: "from-zinc-300  to-zinc-500",   text: "text-zinc-300" },
  { key: "aura_color_season",     label: "Color Season",    bar: "from-cyan-400  to-sky-500",    text: "text-cyan-300" },
  { key: "aura_statement",        label: "Statement",       bar: "from-fuchsia-500 to-purple-500", text: "text-fuchsia-300" },
  { key: "aura_photo_readiness",  label: "Photo Readiness", bar: "from-amber-400 to-orange-500", text: "text-amber-300" },
  { key: "aura_score",            label: "Overall Aura",    bar: "from-cyan-400 to-teal-400",    text: "text-cyan-300" },
];

const VERDICT_LABEL = (s?: number) => {
  const score = s ?? 0;
  if (score >= 85) return "Excellent";
  if (score >= 70) return "Strong";
  if (score >= 55) return "Promising";
  if (score >= 40) return "Developing";
  return "Emerging";
};

interface RecentRow {
  id: string;
  image_url: string | null;
  user_photo_url: string | null;
  generated_image_url: string | null;
}

export const AuraHomeDesktop = (props: Props) => {
  const {
    photoPreview, scanning, scanLabel, result,
    onPickCamera, onPickUpload, onClearPhoto, onRunAura, onNavHistory,
    avatarUrl, initials, onBuyCredits,
  } = props;

  const score = result?.aura_score ?? null;
  const verdictWord = VERDICT_LABEL(score ?? 0).toUpperCase();
  // Stroke math for the synergy ring (compact)
  const ringSize = 168;
  const stroke = 12;
  const radius = (ringSize - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const dash = score != null ? (score / 100) * circ : 0;

  return (
    <div className="relative min-h-screen">
      {/* Top bar — eyebrow + title + search + bell + avatar */}
      <StitchDesktopHeader
        eyebrow="Analysis Engine"
        title="Aura Analysis"
        onBuyCredits={onBuyCredits}
      />

      <div className="mx-auto w-full max-w-7xl px-8 py-6">
        {/* ===== TOP ROW: Composer (4) + Synergy ring (3) + AI Synthesis (5) ===== */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          {/* Composer / current capture */}
          <Section className="overflow-hidden p-0 lg:col-span-4">
            <div className="relative aspect-[3/4] w-full bg-background/40">
              {photoPreview ? (
                <>
                  <img
                    src={photoPreview}
                    alt="Your photo"
                    className={cn(
                      "h-full w-full object-cover object-top transition-all duration-500",
                      scanning && "scale-[1.02]",
                    )}
                  />
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/10"
                  />
                  {!scanning && !result && (
                    <button
                      type="button"
                      onClick={onClearPhoto}
                      className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-background/80 text-foreground shadow-soft backdrop-blur-md transition-all hover:scale-110"
                      aria-label="Remove photo"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  {scanning && (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-primary/10" />
                      <div className="aura-scanline pointer-events-none absolute inset-x-0 h-24" />
                      <div className="pointer-events-none absolute inset-4">
                        <span className="absolute left-0 top-0 h-5 w-5 border-l-2 border-t-2 border-primary" />
                        <span className="absolute right-0 top-0 h-5 w-5 border-r-2 border-t-2 border-primary" />
                        <span className="absolute bottom-0 left-0 h-5 w-5 border-b-2 border-l-2 border-primary" />
                        <span className="absolute bottom-0 right-0 h-5 w-5 border-b-2 border-r-2 border-primary" />
                      </div>
                      <div className="absolute inset-x-6 bottom-6 flex items-center justify-center">
                        <div className="flex items-center gap-2 rounded-full border border-primary/30 bg-background/80 px-4 py-2 text-xs font-semibold text-foreground backdrop-blur-md">
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                          {scanLabel}
                        </div>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-6 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/25 to-accent/15 text-primary shadow-soft ring-1 ring-primary/20">
                    <Sparkles className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="font-display text-sm font-bold">Add your photo</p>
                    <p className="mt-1 text-xs text-muted-foreground">Clean, full-body shot works best.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Composer footer */}
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-white/5 px-4 py-3">
              <div className="flex items-center gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 rounded-full border-white/10 bg-white/[0.03] px-3 text-xs hover:border-primary/40"
                  onClick={onPickCamera}
                  disabled={scanning}
                >
                  <Camera className="mr-1.5 h-3.5 w-3.5" />
                  {photoPreview ? "Retake" : "Camera"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 rounded-full border-white/10 bg-white/[0.03] px-3 text-xs hover:border-primary/40"
                  onClick={onPickUpload}
                  disabled={scanning}
                >
                  <Upload className="mr-1.5 h-3.5 w-3.5" />
                  {photoPreview ? "Change" : "Upload"}
                </Button>
              </div>
              <Button
                size="sm"
                className="font-display h-8 rounded-full bg-gradient-to-r from-primary via-primary to-accent px-4 text-[11px] font-bold uppercase tracking-[0.18em] text-primary-foreground shadow-[0_0_24px_-6px_hsl(var(--primary))] hover:shadow-[0_0_32px_-4px_hsl(var(--primary))]"
                onClick={result ? onNavHistory : onRunAura}
                disabled={scanning || (!photoPreview && !result)}
              >
                {scanning ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    Reading…
                  </>
                ) : result ? (
                  <>
                    History
                    <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                  </>
                ) : (
                  <>
                    <Wand2 className="mr-1.5 h-3.5 w-3.5" />
                    Read Aura
                  </>
                )}
              </Button>
            </div>
          </Section>

          {/* CURRENT SYNERGY ring — compact */}
          <Section className="flex flex-col lg:col-span-3">
            <p className="font-display text-center text-[10px] font-semibold uppercase tracking-[0.32em] text-muted-foreground">
              Current Synergy
            </p>

            <div className="relative mt-3 flex flex-1 items-center justify-center">
              <svg width={ringSize} height={ringSize} className="-rotate-90">
                <circle
                  cx={ringSize / 2}
                  cy={ringSize / 2}
                  r={radius}
                  stroke="hsl(var(--muted))"
                  strokeOpacity={0.25}
                  strokeWidth={stroke}
                  fill="none"
                />
                {score != null && (
                  <circle
                    cx={ringSize / 2}
                    cy={ringSize / 2}
                    r={radius}
                    stroke="url(#auraSynergyGradient)"
                    strokeWidth={stroke}
                    strokeLinecap="round"
                    fill="none"
                    strokeDasharray={`${dash} ${circ}`}
                    style={{ transition: "stroke-dasharray 900ms ease-out" }}
                  />
                )}
                <defs>
                  <linearGradient id="auraSynergyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="hsl(var(--primary))" />
                    <stop offset="100%" stopColor="hsl(var(--accent))" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                {score != null ? (
                  <>
                    <span className="font-display text-5xl font-bold tabular-nums text-foreground">
                      {Math.round(score)}
                    </span>
                    <span className="font-display mt-0.5 text-[10px] font-semibold uppercase tracking-[0.3em] text-primary">
                      {verdictWord}
                    </span>
                  </>
                ) : scanning ? (
                  <>
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <span className="font-display mt-2 text-[9px] font-semibold uppercase tracking-[0.3em] text-muted-foreground">
                      Reading…
                    </span>
                  </>
                ) : (
                  <>
                    <span className="font-display text-4xl font-bold tabular-nums text-muted-foreground/50">
                      —
                    </span>
                    <span className="font-display mt-1 text-[9px] font-semibold uppercase tracking-[0.3em] text-muted-foreground">
                      Awaiting Photo
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 border-t border-white/5 pt-3">
              <Stat label="Rank" value={score != null ? `Top ${100 - Math.round(score)}%` : "—"} />
              <Stat label="Trend" value={score != null ? `+${(score / 10).toFixed(1)}%` : "—"} accent />
            </div>
          </Section>

          {/* AI Synthesis — moved next to ring */}
          <Section className="flex flex-col border-primary/40 shadow-[0_0_36px_-12px_hsl(var(--primary))] lg:col-span-5">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 text-primary">
                <BrainCircuit className="h-4 w-4" />
              </span>
              <p className="font-display text-[11px] font-bold uppercase tracking-[0.28em] text-primary">
                AI Synthesis
              </p>
            </div>
            <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground/90">
              {result?.aura_verdict ? (
                <>"{result.aura_verdict}"</>
              ) : scanning ? (
                "Composing your aura — analysing silhouette, color season and statement factor…"
              ) : photoPreview ? (
                <>Ready to read your aura. Press <span className="font-semibold text-primary">Read Aura</span> to run a deep editorial analysis.</>
              ) : (
                <>Add a clean, full-body photo and FitAura's analysis engine will synthesize your <span className="font-semibold text-foreground">silhouette, color season, statement factor</span> and photo-readiness into a single aura score.</>
              )}
            </p>
            {result?.aura_tips?.[0] && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-400/20 bg-amber-400/5 p-2.5">
                <Zap className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" />
                <p className="text-xs leading-relaxed text-foreground/80">{result.aura_tips[0]}</p>
              </div>
            )}
          </Section>
        </div>

        {/* ===== SECOND ROW: Breakdown (7) + Insights stack (5) ===== */}
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-12">
          <Section className="lg:col-span-7">
            <p className="font-display mb-4 text-[11px] font-semibold uppercase tracking-[0.32em] text-muted-foreground">
              Aura Breakdown
            </p>
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              {METRICS.map((m) => {
                const v = result ? (result[m.key] as number | undefined) : undefined;
                const safe = typeof v === "number" ? Math.max(0, Math.min(100, v)) : 0;
                return (
                  <div key={m.key as string}>
                    <div className="flex items-center justify-between">
                      <span className="font-display text-[10px] font-semibold uppercase tracking-[0.26em] text-muted-foreground">
                        {m.label}
                      </span>
                      <span className={cn("font-display text-xs font-bold tabular-nums", typeof v === "number" ? m.text : "text-muted-foreground/50")}>
                        {typeof v === "number" ? `${Math.round(safe)}%` : "—"}
                      </span>
                    </div>
                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white/5">
                      <div
                        className={cn("h-full rounded-full bg-gradient-to-r transition-[width] duration-700 ease-out", m.bar)}
                        style={{ width: `${safe}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>

          {/* Insight cards stacked */}
          <div className="grid grid-cols-1 gap-3 lg:col-span-5">
            <InsightCard
              icon={<Sparkles className="h-4 w-4" />}
              tone="primary"
              title="Style Evolution"
              body={
                result
                  ? "Your silhouette is trending toward refined editorial cuts."
                  : "AI will detect gradual transitions in your style after a few captures."
              }
            />
            <InsightCard
              icon={<Palette className="h-4 w-4" />}
              tone="accent"
              title="Palette Optimization"
              body={
                result
                  ? "Skin-tone radiance suggests cool-toned violets and deep teals for peak impact."
                  : "Get personalised palette recommendations once your aura is read."
              }
            />
          </div>
        </div>

        {/* ===== Recent captures (full width, bottom) ===== */}
        <div className="mt-4">
          <Section>
            <div className="flex items-center justify-between">
              <p className="font-display text-xs font-bold uppercase tracking-[0.28em] text-foreground">
                Recent Captures
              </p>
              <button
                type="button"
                onClick={onNavHistory}
                className="font-display inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.28em] text-primary transition-colors hover:text-accent"
              >
                View Archive
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
            <RecentCaptures onPickUpload={onPickUpload} refreshKey={result?.tryonId ?? ""} />
          </Section>
        </div>
      </div>
    </div>
  );
};

/* ---------- Local helpers ---------- */

const Section = ({ children, className }: { children: ReactNode; className?: string }) => (
  <section className={cn("rounded-2xl border border-white/10 bg-card/40 p-5 backdrop-blur-xl", className)}>
    {children}
  </section>
);

const Stat = ({ label, value, accent }: { label: string; value: string; accent?: boolean }) => (
  <div className="text-center">
    <p className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-muted-foreground">
      {label}
    </p>
    <p className={cn("font-display mt-1.5 text-lg font-bold tabular-nums", accent ? "text-cyan-300" : "text-foreground")}>
      {value}
    </p>
  </div>
);

const InsightCard = ({
  icon, tone, title, body,
}: { icon: ReactNode; tone: "primary" | "accent" | "warm"; title: string; body: string }) => {
  const toneMap = {
    primary: { bg: "bg-primary/15", text: "text-primary", ring: "ring-primary/20" },
    accent:  { bg: "bg-accent/15",  text: "text-accent",  ring: "ring-accent/20" },
    warm:    { bg: "bg-amber-400/15", text: "text-amber-300", ring: "ring-amber-400/20" },
  } as const;
  const t = toneMap[tone];
  return (
    <div className="group flex items-start gap-3 rounded-2xl border border-white/10 bg-card/40 p-4 backdrop-blur-xl transition-all hover:-translate-y-0.5 hover:border-primary/30">
      <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1", t.bg, t.text, t.ring)}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-display text-sm font-bold text-foreground">{title}</p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{body}</p>
      </div>
    </div>
  );
};

/** Pulls last 3 aura captures for the user. Read-only. */
const RecentCaptures = ({ onPickUpload, refreshKey }: { onPickUpload: () => void; refreshKey: string }) => {
  const { user } = useAuth();
  const [rows, setRows] = useState<RecentRow[] | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    supabase
      .from("tryons")
      .select("id, user_photo_url, generated_image_url, kind, status")
      .eq("user_id", user.id)
      .eq("kind", "aura")
      .eq("status", "ready")
      .order("created_at", { ascending: false })
      .limit(3)
      .then(({ data }) => {
        if (cancelled) return;
        setRows((data ?? []).map((r) => ({
          id: r.id,
          image_url: null,
          user_photo_url: r.user_photo_url,
          generated_image_url: r.generated_image_url,
        })));
      });
    return () => { cancelled = true; };
  }, [user, refreshKey]);

  const tiles = rows ?? [];

  return (
    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
      {tiles.map((row) => (
        <RecentTile key={row.id} row={row} />
      ))}
      {/* Upload tile */}
      <button
        type="button"
        onClick={onPickUpload}
        className="group flex aspect-[3/4] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 bg-white/[0.02] text-muted-foreground transition-all hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
      >
        <Plus className="h-5 w-5" />
        <span className="font-display text-[9px] font-bold uppercase tracking-[0.3em]">
          Upload
        </span>
      </button>
      {/* Skeletons while loading */}
      {rows == null && Array.from({ length: 3 }).map((_, i) => (
        <div key={`s-${i}`} className="aspect-[3/4] animate-pulse rounded-xl border border-white/10 bg-white/[0.04]" />
      ))}
    </div>
  );
};

const RecentTile = ({ row }: { row: RecentRow }) => {
  // Prefer the generated aura visual; fall back to the user-uploaded photo.
  const generatedBucket: "tryon-results" = "tryon-results";
  const userBucket: "user-photos" = "user-photos";
  const generatedSrc = useSignedImage(generatedBucket, row.generated_image_url);
  const userSrc = useSignedImage(userBucket, row.user_photo_url);
  const src = generatedSrc || userSrc;

  return (
    <div className="group relative aspect-[3/4] overflow-hidden rounded-xl border border-white/10 bg-background/40">
      {src ? (
        <img src={src} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-muted-foreground/40">
          <Sparkles className="h-5 w-5" />
        </div>
      )}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent"
      />
    </div>
  );
};
